import posthog from "posthog-js";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import { gtmEvent, posthogEvent, generateEventId } from "./channels";

/**
 * TrackingCore owns the cross-event state (user identity, native device ids) and
 * the single `track()` fan-out that every event method funnels through. The
 * domain-specific event methods live in sibling "method bag" modules
 * (onboarding.ts, booking.ts, growth.ts, discovery.ts) and are merged onto the
 * core singleton in index.ts via Object.assign — each uses a `this: TrackingCore`
 * receiver so `tracking.someEvent()` binds back here and can call `this.track()`.
 */
export class TrackingCore {
  userData:
    | {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
      }
    | undefined = undefined;

  // Native device identifiers (IDFA/GAID + AppsFlyer UID), pulled over the bridge
  // and stamped onto every event. Native checkout moved from RevenueCat IAP to
  // Stripe, and RevenueCat used to be what forwarded the device identifier to our
  // analytics — this restores it for PostHog / GTM / server-side (Meta CAPI, GA4).
  // AppsFlyer's own native events already carry the identifier, so it isn't added
  // to the bridge purchase/track forwards. Empty object on web.
  deviceIds: { advertising_id?: string; appsflyer_id?: string } = {};

  /**
   * Pull native device identifiers over the bridge and cache them so subsequent
   * events carry them. On iOS the advertising id only exists once ATT is granted
   * (the prompt resolves shortly after launch), so we retry once if it isn't
   * ready yet. No-op on web.
   */
  async loadNativeDeviceIds(): Promise<void> {
    if (!bridge.isNative) return;

    const fetchOnce = async (): Promise<boolean> => {
      try {
        const ids = await bridge.request<{
          advertisingId?: string | null;
          appsFlyerId?: string | null;
          trackingStatus?: string;
        }>(BridgeChannels.TRACKING, BridgeActions.GET_DEVICE_IDS);

        console.log("[Tracking] GET_DEVICE_IDS response", {
          advertisingId: ids?.advertisingId ?? null,
          appsFlyerId: ids?.appsFlyerId ?? null,
          trackingStatus: ids?.trackingStatus ?? null,
        });

        this.deviceIds = {
          ...(ids?.advertisingId ? { advertising_id: ids.advertisingId } : {}),
          ...(ids?.appsFlyerId ? { appsflyer_id: ids.appsFlyerId } : {}),
        };

        if (Object.keys(this.deviceIds).length > 0) {
          posthog.setPersonProperties(this.deviceIds);
          // Push once into the dataLayer so GTM Data Layer Variables pick them
          // up for all subsequent tag evaluations in this session.
          gtmEvent("device_ids_loaded", this.deviceIds);
        }

        return !!ids?.advertisingId;
      } catch (e) {
        console.warn("[Tracking] Failed to load native device ids", e);
        return false;
      }
    };

    const gotAdvertisingId = await fetchOnce();
    // ATT may still be pending right after launch — retry once after a delay.
    if (!gotAdvertisingId) {
      setTimeout(fetchOnce, 4000);
    }
  }

  /**
   * Native device identifiers in the shape the checkout API expects, or
   * undefined on web / before they've loaded. Passed to the create-payment call
   * so they ride into the Stripe intent metadata and the webhook can fire
   * AppsFlyer S2S purchase events scoped to the device that made the purchase.
   */
  async deviceIdentifiersForCheckout(): Promise<
    { appsflyerId?: string; advertisingId?: string; platform?: "ios" | "android" } | undefined
  > {
    if (!bridge.isNative) return undefined;

    // Re-fetch fresh at checkout time — ATT prompt may have resolved after the
    // initial loadNativeDeviceIds() call, so don't rely on the cached value.
    try {
      const ids = await bridge.request<{
        advertisingId?: string | null;
        appsFlyerId?: string | null;
      }>(BridgeChannels.TRACKING, BridgeActions.GET_DEVICE_IDS);

      if (ids?.advertisingId) this.deviceIds.advertising_id = ids.advertisingId;
      if (ids?.appsFlyerId) this.deviceIds.appsflyer_id = ids.appsFlyerId;
    } catch (e) {
      console.warn("[Tracking] deviceIdentifiersForCheckout: bridge fetch failed", e);
    }

    const platform = bridge.platform;
    const payload = {
      ...(this.deviceIds.appsflyer_id ? { appsflyerId: this.deviceIds.appsflyer_id } : {}),
      ...(this.deviceIds.advertising_id ? { advertisingId: this.deviceIds.advertising_id } : {}),
      ...(platform === "ios" || platform === "android" ? { platform: platform as "ios" | "android" } : {}),
    };
    return Object.keys(payload).length > 0 ? payload : undefined;
  }

  /**
   * Track an event - sends to client-side GTM, PostHog, and native Facebook SDK.
   * This is the single fan-out every event method funnels through.
   */
  track(eventName: string, eventParams?: Record<string, any>) {
    const eventId = generateEventId();
    const params = { ...eventParams, ...this.deviceIds };

    // Push to client-side GTM immediately (for real-time analytics)
    gtmEvent(eventName, {
      ...params,
      event_id: eventId,
      user_data: this.userData,
    });
    console.log("sent event to GTM", eventName, params);
    posthogEvent(eventName, {
      ...params,
      distinctId: eventId,
      userData: this.userData,
      platform: bridge.isNative ? bridge.platform : 'web',
    });

    // Forward to native Facebook App Events SDK via bridge
    if (bridge.isNative) {
      bridge.emit(BridgeChannels.TRACKING, BridgeActions.TRACK_EVENT, {
        name: eventName,
        parameters: params,
      });
    }
  }

  initialized() {
    this.track("auth_initialized");
    if (this.userData) {
      posthog.identify(this.userData?.id, {
        email: this.userData?.email,
        first_name: this.userData?.first_name,
        last_name: this.userData?.last_name,
        platform: bridge.isNative ? bridge.platform : 'web',
        ...(bridge.isNative && { app_version: bridge.appBuildNumber }),
        ...this.deviceIds,
      });

      // Sync user data to native Facebook SDK
      if (bridge.isNative) {
        bridge.emit(
          BridgeChannels.TRACKING,
          BridgeActions.SET_USER_PROPERTIES,
          {
            userId: this.userData.id,
            email: this.userData.email,
            firstName: this.userData.first_name,
            lastName: this.userData.last_name,
          }
        );
      }
    }
  }

  trackPage(page: string) {
    this.track("page_view", {
      page_title: page,
      page_location: window.location.href,
    });
  }

  trackFlow(page: string) {
    this.track("flow_view", {
      page_title: page,
      page_location: window.location.href,
    });
  }
}

export const core = new TrackingCore();
